import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '../components/Modal';
import { api, ApiError, type WheelSector, type WheelState } from '../lib/api';
import { brand } from '../lib/brand';
import { play } from '../lib/sounds';

type Props = {
    /** Opens the top-up flow (a deposit this week unlocks the wheel). */
    onTopUp?: () => void;
    /** Jumps to the referrals tab. */
    onInvite?: () => void;
    /** Called after a BSP prize so the header balance can refresh. */
    onBalanceChanged?: () => void;
    isRoot?: boolean;
};

/** BogStars palette for prize sectors; "Ничего" is always the dark slice. */
const PRIZE_COLORS = ['#FFD700', '#5d5dff', '#2dcc70', '#fd7e14', '#a78bfa', '#22d3ee', '#FF4D4D', '#f472b6'];
const NOTHING_COLOR = '#262626';

function colorMap(state: WheelState | null): Map<number, string> {
    const m = new Map<number, string>();
    let i = 0;
    for (const p of state?.prizes ?? []) {
        m.set(p.id, p.kind === 'nothing' ? NOTHING_COLOR : PRIZE_COLORS[i++ % PRIZE_COLORS.length]!);
    }
    return m;
}

const textOn = (bg: string) => (bg === '#FFD700' || bg === '#2dcc70' || bg === '#22d3ee' ? '#121212' : '#ffffff');

const SIZE = 340;
const TAU = Math.PI * 2;

function haptic(kind: 'tick' | 'win' | 'lose' | 'tap') {
    try {
        const hf = (window as unknown as { Telegram?: { WebApp?: { HapticFeedback?: {
            selectionChanged?: () => void; impactOccurred?: (s: string) => void; notificationOccurred?: (t: string) => void;
        } } } }).Telegram?.WebApp?.HapticFeedback;
        if (!hf) return;
        if (kind === 'tick') hf.selectionChanged?.();
        else if (kind === 'tap') hf.impactOccurred?.('medium');
        else hf.notificationOccurred?.(kind === 'win' ? 'success' : 'warning');
    } catch { /* not in Telegram */ }
}

/**
 * Колесо фортуны (Бонусы → Колесо).
 *
 * The server decides the prize; the wheel only animates to the sector it
 * returned. Spinning needs a deposit this week; one free spin per MSK day,
 * extra spins come from deposits ≥ the threshold and every N referrals.
 */
export function WheelView({ onTopUp, onInvite, onBalanceChanged, isRoot = false }: Props) {
    const [state, setState] = useState<WheelState | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [spinning, setSpinning] = useState(false);
    const [result, setResult] = useState<WheelSector | null>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const rotation = useRef(0);
    const sectorsRef = useRef<WheelSector[]>([]);
    const colorsRef = useRef<Map<number, string>>(new Map());

    // Doesn't clear `err`: it also runs right after a refused spin, and wiping
    // the message there meant the user never saw why the spin failed.
    const load = useCallback(async () => {
        try { setState(await api.getWheel()); }
        // 404 here means the backend predates the wheel (frontend deployed alone).
        catch (e) { setErr(e instanceof ApiError ? (e.status === 404 ? 'not_deployed' : e.code) : String(e)); }
    }, []);
    useEffect(() => { void load(); }, [load]);

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const dpr = Math.min(window.devicePixelRatio || 1, 3);
        if (canvas.width !== SIZE * dpr) { canvas.width = SIZE * dpr; canvas.height = SIZE * dpr; }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, SIZE, SIZE);

        const segs = sectorsRef.current;
        const n = segs.length;
        const R = SIZE / 2 - 10;
        const c = SIZE / 2;
        if (n === 0) {
            ctx.beginPath(); ctx.arc(c, c, R, 0, TAU);
            ctx.fillStyle = '#1e1e1e'; ctx.fill();
            return;
        }
        const seg = TAU / n;
        ctx.save();
        ctx.translate(c, c);
        ctx.rotate(rotation.current);
        for (let i = 0; i < n; i++) {
            const a0 = -Math.PI / 2 + i * seg;
            ctx.beginPath(); ctx.moveTo(0, 0); ctx.arc(0, 0, R, a0, a0 + seg); ctx.closePath();
            ctx.fillStyle = colorsRef.current.get(segs[i]!.prizeId) ?? NOTHING_COLOR;
            ctx.fill();
            ctx.lineWidth = 2; ctx.strokeStyle = '#121212'; ctx.stroke();
        }
        const fs = n > 18 ? 10 : n > 12 ? 12 : 14;
        for (let i = 0; i < n; i++) {
            const mid = -Math.PI / 2 + (i + 0.5) * seg;
            const m = ((mid % TAU) + TAU) % TAU;
            const flip = m > Math.PI / 2 && m < Math.PI * 1.5;
            const bg = colorsRef.current.get(segs[i]!.prizeId) ?? NOTHING_COLOR;
            ctx.save();
            ctx.rotate(mid + (flip ? Math.PI : 0));
            ctx.textBaseline = 'middle';
            ctx.textAlign = flip ? 'left' : 'right';
            ctx.fillStyle = bg === NOTHING_COLOR ? '#777' : textOn(bg);
            ctx.font = `900 ${fs}px Inter, 'Segoe UI', sans-serif`;
            const label = segs[i]!.kind === 'deposit_bonus' ? `+${segs[i]!.value}% ДЕП` : segs[i]!.label.toUpperCase();
            ctx.fillText(label, flip ? -(R - 14) : R - 14, 0);
            ctx.restore();
        }
        ctx.restore();
        // Gold rim
        ctx.beginPath(); ctx.arc(c, c, R + 2, 0, TAU);
        ctx.strokeStyle = '#FFD700'; ctx.lineWidth = 4; ctx.stroke();
        ctx.beginPath(); ctx.arc(c, c, R + 7, 0, TAU);
        ctx.strokeStyle = 'rgba(255,215,0,0.25)'; ctx.lineWidth = 3; ctx.stroke();
    }, []);

    // Keep the drawing in sync with the latest pool (not while spinning — the
    // spin sets its own sectors and redraws every frame).
    useEffect(() => {
        if (!state || spinning) return;
        sectorsRef.current = state.sectors;
        colorsRef.current = colorMap(state);
        draw();
    }, [state, spinning, draw]);

    const animateTo = (index: number, n: number) => new Promise<void>((resolve) => {
        const seg = TAU / n;
        const jitter = (Math.random() - 0.5) * seg * 0.6;
        const target = -(index + 0.5) * seg + jitter;
        const start = rotation.current;
        let delta = target - start;
        delta = ((delta % TAU) + TAU) % TAU;
        const total = delta + (5 + Math.floor(Math.random() * 3)) * TAU;
        const duration = 4600;
        const t0 = performance.now();
        let lastIdx = -1;
        let lastTick = 0;
        const frame = (now: number) => {
            const t = Math.min(1, (now - t0) / duration);
            rotation.current = start + total * (1 - Math.pow(1 - t, 3.4));
            draw();
            const idx = Math.floor((((-rotation.current) % TAU) + TAU) % TAU / seg) % n;
            if (idx !== lastIdx && now - lastTick > 60 && t < 0.98) { lastIdx = idx; lastTick = now; haptic('tick'); }
            if (t < 1) requestAnimationFrame(frame); else resolve();
        };
        requestAnimationFrame(frame);
    });

    const doSpin = async () => {
        if (spinning || !state || state.spinsAvailable < 1) return;
        setSpinning(true); setErr(null);
        haptic('tap');
        try {
            const r = await api.spinWheel();
            sectorsRef.current = r.sectors;           // the layout the server used
            colorsRef.current = colorMap(r.state);
            play('box-open');
            await animateTo(r.index, r.sectors.length);
            const won = r.prize.kind !== 'nothing';
            play(won ? 'win' : 'lose');
            haptic(won ? 'win' : 'lose');
            setResult(r.prize);
            setState(r.state);
            if (r.prize.kind === 'bsp') onBalanceChanged?.();
        } catch (e) {
            const code = e instanceof ApiError ? e.code : '';
            setErr(
                code === 'deposit_required' ? 'Чтобы крутить, нужен депозит на этой неделе.' :
                code === 'no_spins' || code === 'already_spinning' ? 'Круток пока нет.' :
                code === 'wheel_empty' ? 'Колесо ещё не настроено.' :
                code === 'too_many_requests' ? 'Секунду…' : 'Не получилось прокрутить. Попробуйте ещё раз.',
            );
            void load();
        } finally { setSpinning(false); }
    };

    if (!state) {
        if (!err) return <div className="task-empty">Загружаем…</div>;
        return (
            <div className="task-empty" data-testid="wheel-load-error">
                <p>Не удалось загрузить колесо{err === 'not_deployed'
                    ? ' — сервер ещё не обновлён.' : ` (${err}).`}</p>
                <button type="button" className="action-btn btn-outline-gold" style={{ marginTop: 10 }}
                    onClick={() => { setErr(null); void load(); }}>Повторить</button>
            </div>
        );
    }
    if (state.sectors.length === 0) {
        return (
            <div className="task-empty" data-testid="wheel-empty">
                Колесо скоро заработает.
                {isRoot && <p style={{ marginTop: 8, color: 'var(--accent)' }}>
                    Пул призов пуст: добавьте призы в Админ-панель → Root → Колесо.
                </p>}
            </div>
        );
    }

    const colors = colorMap(state);
    const refsDone = state.refsPerSpin - state.refsToNext;
    const spinLabel = spinning ? 'КРУТИТСЯ…'
        : !state.depositedThisWeek ? 'НУЖЕН ДЕПОЗИТ НА НЕДЕЛЕ'
        : state.spinsAvailable < 1 ? 'КРУТОК НЕТ'
        : state.dailyAvailable ? 'КРУТИТЬ БЕСПЛАТНО' : `КРУТИТЬ · ${state.bonusSpins}`;

    return (
        <div className="wheel-view">
            <div className="wheel-stats">
                <div className="wheel-stat">
                    <div className="wheel-stat-num" data-testid="wheel-spins">{state.spinsAvailable}</div>
                    <div className="wheel-stat-label">Круток</div>
                </div>
                <div className="wheel-stat">
                    <div className="wheel-stat-num">{state.dailyAvailable ? '✓' : '00:00'}</div>
                    <div className="wheel-stat-label">{state.dailyAvailable ? 'Бесплатная' : 'Бесплатная в'}</div>
                </div>
                <div className="wheel-stat">
                    <div className="wheel-stat-num" style={{ color: state.depositedThisWeek ? 'var(--green)' : 'var(--red)' }}>
                        {state.depositedThisWeek ? '✓' : '✗'}
                    </div>
                    <div className="wheel-stat-label">Депозит на неделе</div>
                </div>
            </div>

            {!state.depositedThisWeek && (
                <div className="wheel-gate" data-testid="wheel-gate">
                    <div>
                        <b>Колесо открывается после депозита</b>
                        <span>Пополните баланс на любую сумму на этой неделе — и крутите каждый день.</span>
                        {isRoot && <span>Начисление через «Баланс» в root-панели депозитом не считается.</span>}
                    </div>
                    {onTopUp && <button type="button" className="action-btn btn-gold" onClick={onTopUp}>Пополнить</button>}
                </div>
            )}

            <div className="wheel-wrap">
                <div className="wheel-pointer" />
                <canvas ref={canvasRef} className="wheel-canvas" data-testid="wheel-canvas"
                    style={{ width: '100%', height: '100%' }} />
                <div className="wheel-hub">{brand.hub[0]}<br /><span>{brand.hub[1]}</span></div>
            </div>

            <button type="button" className="action-btn btn-gold wheel-spin-btn"
                disabled={spinning || state.spinsAvailable < 1} onClick={doSpin}>
                {spinLabel}
            </button>
            {err && <p className="popup-text" style={{ color: 'var(--red)', textAlign: 'center' }}>{err}</p>}

            {state.bonuses.map((b) => (
                <div key={b.expiresAt} className="wheel-bonus">
                    🎁 +{b.pct}% к следующему депозиту · до {fmt(b.expiresAt)}
                </div>
            ))}

            <h3 className="wheel-h">Как получить крутки</h3>
            <div className="wheel-ways">
                <div className="wheel-way">
                    <span className="wheel-way-icon">📅</span>
                    <div className="wheel-way-txt">
                        <b>Каждый день</b>
                        <span>1 бесплатная крутка в сутки, сброс в 00:00 МСК</span>
                    </div>
                    <span className={`wheel-way-tag ${state.dailyAvailable ? 'on' : ''}`}>
                        {state.dailyAvailable ? 'доступна' : 'завтра'}
                    </span>
                </div>
                <div className="wheel-way">
                    <span className="wheel-way-icon">💳</span>
                    <div className="wheel-way-txt">
                        <b>Депозит от {state.depositMin} BSP</b>
                        <span>+1 крутка за каждое такое пополнение</span>
                    </div>
                    {onTopUp && <button type="button" className="wheel-way-btn" onClick={onTopUp}>Пополнить</button>}
                </div>
                <div className="wheel-way" data-testid="wheel-refs">
                    <span className="wheel-way-icon">👥</span>
                    <div className="wheel-way-txt">
                        <b>{state.refsPerSpin} рефералов = 1 крутка</b>
                        <span>Бесконечно · ещё {state.refsToNext} до следующей</span>
                        <div className="task-progress-bar" style={{ marginTop: 6 }}>
                            <div style={{ width: `${Math.round((refsDone / state.refsPerSpin) * 100)}%`, background: 'var(--green)' }} />
                        </div>
                    </div>
                    {onInvite && <button type="button" className="wheel-way-btn" onClick={onInvite}>Пригласить</button>}
                </div>
            </div>

            <h3 className="wheel-h">Призы</h3>
            <div className="wheel-legend">
                {state.prizes.map((p) => (
                    <div key={p.id} className="wheel-legend-item">
                        <span className="wheel-dot" style={{ background: colors.get(p.id) }} />
                        <span>{p.label}</span>
                        <span className="wheel-chance">{p.chance}%</span>
                    </div>
                ))}
            </div>

            {state.recent.length > 0 && (
                <>
                    <h3 className="wheel-h">Мои крутки</h3>
                    <div className="wheel-history">
                        {state.recent.map((s, i) => (
                            <div key={i} className="wheel-history-row">
                                <span style={{ color: s.kind === 'nothing' ? 'var(--text-dim)' : 'var(--accent)', fontWeight: 900 }}>{s.label}</span>
                                <span className="text-muted f-12">{s.source === 'daily' ? 'бесплатная' : 'бонусная'} · {fmt(s.createdAt)}</span>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {result && (
                <Modal title="Колесо" onClose={() => setResult(null)}>
                    <div className="text-center" data-testid="wheel-result">
                        <div className="popup-hero">{result.kind === 'nothing' ? '🌫️' : result.kind === 'bsp' ? '💰' : '🎁'}</div>
                        <p className="popup-text fw-900" style={{ fontSize: 18, color: result.kind === 'nothing' ? 'var(--text-muted)' : 'var(--accent)' }}>
                            {result.kind === 'nothing' ? 'В этот раз пусто' : result.label}
                        </p>
                        <p className="popup-text text-muted f-12">
                            {result.kind === 'bsp' && 'Уже на балансе.'}
                            {result.kind === 'deposit_bonus' && 'Добавится к следующему пополнению.'}
                            {result.kind === 'nothing' && 'Повезёт в следующий раз!'}
                        </p>
                        <button type="button" className="action-btn btn-gold" onClick={() => setResult(null)}>ОК</button>
                    </div>
                </Modal>
            )}
        </div>
    );
}

function fmt(iso: string) {
    try {
        return new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
}
